import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet, Loader2, Upload,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  RUNNING_STATUSES, TERMINAL_STATUSES, confirmBulkUpload, downloadBulkTemplate,
  getAnchors, getBulkUploadHistory, getBulkUploadStatus, uploadBulkSubscriptions,
} from "../services/bulkSubscriptionService";
import { StatCard, Toast, getCurrentRole } from "../components/diagnostic/primitives";

const POLL_INTERVAL_MS = 3000;

const STATUS_STYLE = {
  validated: "bg-blue-50 text-blue-700 ring-blue-600/20",
  invalid: "bg-red-50 text-red-700 ring-red-600/20",
  queued: "bg-amber-50 text-amber-700 ring-amber-600/20",
  running: "bg-amber-50 text-amber-700 ring-amber-600/20",
  success: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  partial: "bg-amber-50 text-amber-700 ring-amber-600/20",
  failed: "bg-red-50 text-red-700 ring-red-600/20",
};

const StatusPill = ({ status }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${
      STATUS_STYLE[status] || "bg-slate-100 text-slate-600 ring-slate-500/20"
    }`}
  >
    {status}
  </span>
);

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const BulkIssueSubscriptions = () => {
  const navigate = useNavigate();
  const canAccess = getCurrentRole() === "SuperAdmin";

  const [anchors, setAnchors] = useState([]);
  const [anchorId, setAnchorId] = useState("");
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [armed, setArmed] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const notify = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadHistory = useCallback(async (forAnchor) => {
    try {
      setHistory(await getBulkUploadHistory(forAnchor));
    } catch (error) {
      notify("error", error.message);
    }
  }, [notify]);

  useEffect(() => {
    if (!canAccess) return;
    getAnchors()
      .then(setAnchors)
      .catch((error) => notify("error", error.message));
    loadHistory();
  }, [canAccess, loadHistory, notify]);

  // Poll only while the server is actually working. A `validated` job is waiting
  // on the admin, not on us, so polling it would spin forever.
  useEffect(() => {
    if (!job || !RUNNING_STATUSES.includes(job.status)) {
      clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await getBulkUploadStatus(job.id || job.job_id);
        setJob(fresh);
        if (TERMINAL_STATUSES.includes(fresh.status)) {
          clearInterval(pollRef.current);
          loadHistory(anchorId);
          notify(fresh.rows_failed ? "error" : "success",
            `Finished — ${fresh.rows_issued} issued, ${fresh.rows_failed} failed`);
        }
      } catch (error) {
        clearInterval(pollRef.current);
        notify("error", error.message);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [job, anchorId, loadHistory, notify]);

  const handleUpload = async () => {
    if (!anchorId || !file) {
      notify("error", "Pick an anchor and a file first");
      return;
    }
    setUploading(true);
    try {
      const result = await uploadBulkSubscriptions(anchorId, file);
      setJob({ ...result, id: result.job_id });
      notify(
        result.can_confirm ? "success" : "error",
        result.can_confirm
          ? `${result.rows_valid} of ${result.total_rows} rows are ready to issue`
          : "No rows passed validation — nothing can be issued from this file",
      );
      loadHistory(anchorId);
    } catch (error) {
      notify("error", error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    // Confirmation is a second click on the button below, not window.confirm.
    // A browser that has been told to "prevent this page from creating
    // additional dialogs" makes window.confirm return false without showing
    // anything, and the button then does nothing at all with no error — which
    // is exactly how this looked when it "stopped working".
    setConfirming(true);
    try {
      const result = await confirmBulkUpload(job.id || job.job_id);
      setJob((current) => ({ ...current, ...result, id: current.id || current.job_id }));
      setArmed(false);
      notify("success", `Issuing ${result.rows_valid} subscriptions…`);
    } catch (error) {
      notify("error", error.message);
    } finally {
      setConfirming(false);
    }
  };

  const handleTemplate = async () => {
    try {
      await downloadBulkTemplate();
    } catch (error) {
      notify("error", error.message);
    }
  };

  if (!canAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-slate-700 font-medium">Only Super Admins can issue subscriptions in bulk.</p>
        </div>
      </div>
    );
  }

  const errors = job?.errors || [];
  // contract: internalapi/views/user_type/shared/subscription_bulk/status.py:67 emits
  // row.status verbatim from the model — 'pending' | 'issued' | 'failed' | 'skipped'
  // (application/models/subscription_bulk_upload.py ROW_STATUS_*), lowercase, not a label.
  const failedRows = (job?.rows || []).filter((row) => row.status === "failed");

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <Toast toast={toast} />

      {/* An explicit destination, not navigate(-1): this page is linked to
          directly and reloaded while a run is in progress, and history-back has
          nowhere to go in either case. */}
      <button
        onClick={() => navigate("/subscriber-wallet")}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Home
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Issue Subscriptions in Bulk</h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload a sheet of subscribers for one anchor. The file is checked first — nothing is
            issued until you confirm.
          </p>
        </div>
        <button
          onClick={handleTemplate}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
        >
          <Download className="w-4 h-4" /> Template
        </button>
      </div>

      {/* ---------------- upload ---------------- */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Anchor</span>
            <select
              value={anchorId}
              onChange={(event) => {
                setAnchorId(event.target.value);
                setJob(null);
                loadHistory(event.target.value);
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Select an anchor…</option>
              {anchors.map((anchor) => (
                <option key={anchor.id} value={anchor.id}>
                  {anchor.name} ({anchor.code})
                </option>
              ))}
            </select>
          </label>

          <label className="block md:col-span-2">
            <span className="text-sm font-medium text-slate-700">Subscriber sheet (.xlsx)</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={(event) => {
                setFile(event.target.files[0] || null);
                setJob(null);
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm"
            />
          </label>
        </div>

        <button
          onClick={handleUpload}
          disabled={uploading || !anchorId || !file}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white disabled:opacity-40"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Validate file
        </button>
      </div>

      {/* ---------------- validation result ---------------- */}
      {job && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-5 h-5 text-slate-400" />
              <div>
                <p className="font-medium text-slate-900">
                  {job.original_filename || file?.name} — version {job.version}
                </p>
                <p className="text-xs text-slate-500">{job.anchor || ""}</p>
              </div>
            </div>
            <StatusPill status={job.status} />
          </div>

          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-4">
            <StatCard label="Rows in file" value={job.total_rows ?? 0} icon={FileSpreadsheet}
              accent={{ bg: "bg-slate-50", fg: "text-slate-600", bar: "bg-slate-400" }} />
            <StatCard label="Ready to issue" value={job.rows_valid ?? 0} icon={CheckCircle2}
              accent={{ bg: "bg-blue-50", fg: "text-blue-600", bar: "bg-blue-500" }} />
            <StatCard label="Issued" value={job.rows_issued ?? 0} icon={CheckCircle2}
              accent={{ bg: "bg-emerald-50", fg: "text-emerald-600", bar: "bg-emerald-500" }} />
            <StatCard label="Failed" value={job.rows_failed ?? 0} icon={AlertCircle}
              accent={{ bg: "bg-red-50", fg: "text-red-600", bar: "bg-red-500" }} />
          </div>

          {RUNNING_STATUSES.includes(job.status) && (
            <div className="mb-4">
              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${job.percent ?? 0}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Issuing… {job.rows_processed ?? 0} of {job.rows_valid ?? 0}
              </p>
            </div>
          )}

          {job.can_confirm && !armed && (
            <button
              onClick={() => setArmed(true)}
              disabled={confirming}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white disabled:opacity-40"
            >
              <CheckCircle2 className="w-4 h-4" />
              Confirm and issue {job.rows_valid} subscription{job.rows_valid === 1 ? "" : "s"}
            </button>
          )}

          {job.can_confirm && armed && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-900">
                Issue {job.rows_valid} subscription{job.rows_valid === 1 ? "" : "s"} now? Each one
                creates an invoice and a policy, credits a wallet, and sends the subscriber a
                WhatsApp or SMS. <strong>This cannot be undone.</strong>
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white disabled:opacity-40"
                >
                  {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Yes, issue {job.rows_valid} now
                </button>
                <button
                  onClick={() => setArmed(false)}
                  disabled={confirming}
                  className="px-4 py-2 text-sm text-slate-700 border border-slate-300 rounded-lg disabled:opacity-40"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {(errors.length > 0 || failedRows.length > 0) && (
            <div className="mt-5">
              <p className="text-sm font-medium text-slate-700 mb-2">
                Rows that will not be issued
              </p>
              <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium w-20">Row</th>
                      <th className="text-left px-3 py-2 font-medium w-36">Mobile</th>
                      <th className="text-left px-3 py-2 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((entry) => (
                      <tr key={`e-${entry.row}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-500">{entry.row}</td>
                        {/* Shown as the sheet had it: when the complaint IS the
                            mobile, the cleaned digits would hide the problem. */}
                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{entry.mobile || "—"}</td>
                        <td className="px-3 py-2 text-red-600">{entry.errors.join("; ")}</td>
                      </tr>
                    ))}
                    {failedRows.map((row) => (
                      <tr key={`r-${row.row_number}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-500">{row.row_number}</td>
                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{row.mobile || "—"}</td>
                        <td className="px-3 py-2 text-red-600">{row.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------------- version history ---------------- */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <p className="font-medium text-slate-900 mb-4">
          Upload history{anchorId ? " for this anchor" : ""}
        </p>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Anchor</th>
                  <th className="text-left px-3 py-2 font-medium">Version</th>
                  <th className="text-left px-3 py-2 font-medium">File</th>
                  <th className="text-left px-3 py-2 font-medium">Uploaded by</th>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Issued</th>
                  <th className="text-left px-3 py-2 font-medium">Failed</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-700">{entry.anchor || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">v{entry.version}</td>
                    <td className="px-3 py-2 text-slate-500">{entry.original_filename || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{entry.uploaded_by || "—"}</td>
                    <td className="px-3 py-2 text-slate-500">{formatDateTime(entry.created_at)}</td>
                    <td className="px-3 py-2 text-emerald-700">{entry.rows_issued}</td>
                    <td className="px-3 py-2 text-red-600">{entry.rows_failed}</td>
                    <td className="px-3 py-2"><StatusPill status={entry.status} /></td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => getBulkUploadStatus(entry.id).then(setJob).catch(
                          (error) => notify("error", error.message))}
                        className="text-xs text-slate-600 hover:text-slate-900 underline"
                      >
                        View
                      </button>
                      {entry.source_file_url && (
                        <a href={entry.source_file_url} target="_blank" rel="noreferrer"
                          className="ml-3 text-xs text-slate-600 hover:text-slate-900 underline">
                          File
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkIssueSubscriptions;
